import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { v1 as uuid } from 'uuid';
import {
  FilePreviewLink,
  PreviewUploadedDocsDiv,
  ProgressBar,
  ProgressBarValue,
  RemoveDocumentButton,
  RemoveDocumentIcon,
  SuccessUploadIcon
} from './Styles';
import {
  Subtitle,
  SubtitleDivider,
  UploadDocumentRow,
  UploadedDocumentPreview,
  UploadedDocumentRowLoading
} from '../../Modal/PaymentsOnHoldUploadDocModal/Styles';
import PaymentsOnHoldService from '../../../services/PaymentsOnHoldService';
import Alert from '../../../_shared/components/Alert';
import ConfirmModal from '../../../_shared/components/ConfirmModal';
import TRToast from '../../../_shared/helpers/TRToast';

const FilePreview = ({
  uploadedDocuments,
  uploading,
  documentType,
  removeDocument,
  removeEtrDocument,
  isEtrTrade,
  previouslyUploadedEtrDocs
}) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(0);
  const displayToast = (variant, msg) => {
    TRToast(
      <Alert variant={variant} messages={[<FormattedMessage id={msg} />]} />
    );
  };

  const openConfirmModal = async s3UriTODelete => {
    setDocumentToDelete(s3UriTODelete);
    setIsConfirmModalOpen(true);
  };

  const handleRemoveDocumentClick = () => {
    const documentIdRegex = /(lienRelease|titleOwnership)(\d*)(-)/;
    try {
      const documentIdRegexArr = documentIdRegex.exec(documentToDelete);
      const docId = documentIdRegexArr[2];
      PaymentsOnHoldService.removePaymentOnHoldDoc(docId)
        .then(
          () => {
            removeDocument(documentToDelete);
            displayToast('success', 'paymentsOnHold.deleteDocument.success');
          },
          () => {
            displayToast('error', 'paymentsOnHold.deleteDocument.failed');
          }
        )
        .finally(() => {
          setIsConfirmModalOpen(false);
          setDocumentToDelete(0);
        });
    } catch (e) {
      displayToast('error', 'paymentsOnHold.deleteDocument.failed');
      setIsConfirmModalOpen(false);
      setDocumentToDelete(0);
    }
  };

  const handleRemoveEtrDocumentClick = () => {
    removeEtrDocument(documentToDelete);
    setIsConfirmModalOpen(false);
    setDocumentToDelete(0);
  };

  const shouldDisplayRemoveButton = uploadedDocumentUrl => {
    if (!isEtrTrade) return true;

    const isSubmittedEtrDocument = previouslyUploadedEtrDocs.some(
      uploadedEtrDocument =>
        uploadedEtrDocument.preSignedUrl === uploadedDocumentUrl
    );

    return !isSubmittedEtrDocument;
  };

  return (
    ((uploadedDocuments && uploadedDocuments.length > 0) || uploading) && (
      <>
        <Subtitle>
          {documentType === 0 ? (
            <FormattedMessage id="paymentsOnHold.uploadDocument.uploadedOwnerships" />
          ) : (
            <FormattedMessage id="paymentsOnHold.uploadDocument.uploadedLienReleases" />
          )}
        </Subtitle>
        <SubtitleDivider />
        <PreviewUploadedDocsDiv>
          {uploadedDocuments.map((s3Uri, index) => {
            return (
              <UploadDocumentRow
                key={`paymentsOnHold-uploaded-document-${uuid()}`}
              >
                <UploadedDocumentPreview>
                  <SuccessUploadIcon />
                  <FilePreviewLink
                    href={s3Uri}
                    target="_blank"
                    rel="noreferrer"
                    variant={documentType === 1 ? 'lien' : ''}
                  >
                    {documentType === 0
                      ? `Title Ownership ${index + 1}`
                      : `Lien Release ${index + 1}`}
                  </FilePreviewLink>
                  {shouldDisplayRemoveButton(s3Uri) && (
                    <RemoveDocumentButton
                      data-testid={`file-preview-${index}-remove`}
                      onClick={() => openConfirmModal(s3Uri)}
                    >
                      <RemoveDocumentIcon />
                    </RemoveDocumentButton>
                  )}
                </UploadedDocumentPreview>
                <br />
                <ConfirmModal
                  isOpen={isConfirmModalOpen}
                  modalLogic={[
                    {
                      msg: (
                        <FormattedMessage id="paymentsOnHold.deleteDocument.modal.message" />
                      ),
                      title: (
                        <FormattedMessage id="paymentsOnHold.deleteDocument.modal.title" />
                      ),
                      onConfirm: () => () =>
                        isEtrTrade
                          ? handleRemoveEtrDocumentClick()
                          : handleRemoveDocumentClick(),
                      confirmLabel: <FormattedMessage id="modal.ok" />,
                      cancelLabel: <FormattedMessage id="modal.cancel" />,
                      onCancel: () => () => {
                        setIsConfirmModalOpen(false);
                      }
                    }
                  ]}
                  canContinue={false}
                />
              </UploadDocumentRow>
            );
          })}
          {uploading && (
            <UploadedDocumentRowLoading>
              <ProgressBar data-testid="file-preview-progress">
                <ProgressBarValue />
              </ProgressBar>
            </UploadedDocumentRowLoading>
          )}
        </PreviewUploadedDocsDiv>
      </>
    )
  );
};

export default FilePreview;
